import React from 'react';
import { Loader2 } from 'lucide-react';

const Th = ({ children }) => (
  <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">{children}</th>
);

const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 text-[var(--isp-body)] whitespace-nowrap ${className}`}>{children}</td>
);

const SummaryCard = ({ title, value, loading }) => (
  <div className=" border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl p-5">
    <p className="text-xs text-[var(--isp-body)]">{title}</p>
    <p className="mt-1 text-2xl font-bold text-white">{loading ? '...' : value}</p>
  </div>
);

export const RevenueTab = ({ paymentsLoading, subsLoading, eventsLoading, paymentsData, subsData, eventsData }) => {
  const summary = paymentsData.summary;

  return (
    <div className="mt-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard title="Completed payments" value={summary?.total_completed ?? '—'} loading={paymentsLoading} />
        <SummaryCard title="Revenue (GHS)" value={summary ? (summary.revenue_ghs ?? 0).toFixed(2) : '—'} loading={paymentsLoading} />
        <SummaryCard title="Active subscriptions" value={subsData.summary?.active_count ?? '—'} loading={subsLoading} />
      </div>

      <div className=" border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--isp-glass-border)] flex items-center justify-between">
          <h2 className="text-[var(--isp-heading)] font-semibold">Payments</h2>
          {paymentsLoading ? <span className="text-xs text-[var(--isp-body)]">Loading...</span> : null}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-950/40 text-[var(--isp-body)]">
              <tr><Th>Reference</Th><Th>Email</Th><Th>Status</Th><Th>Currency</Th><Th>Amount (GHS)</Th><Th>Amount (USD)</Th><Th>Created</Th><Th>Refund</Th></tr>
            </thead>
            <tbody>
              {(paymentsData.rows || []).slice(0, 200).map((r) => (
                <tr key={r.reference} className="border-t border-[var(--isp-glass-border)] hover:bg-[var(--isp-glass-inner)]">
                  <Td>{r.reference}</Td><Td>{r.email}</Td><Td>{r.status}</Td><Td>{r.currency || '—'}</Td>
                  <Td>{r.amount_ghs ?? '—'}</Td><Td>{r.amount_usd ?? '—'}</Td>
                  <Td>{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</Td><Td>{r.refund_event || '—'}</Td>
                </tr>
              ))}
              {(!paymentsData.rows || paymentsData.rows.length === 0) && (
                <tr><td colSpan={8} className="text-center py-10 text-[var(--isp-body)]">No payments</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-5 py-3 text-xs text-[var(--isp-body)]">Showing up to 200 most recent results.</div>
      </div>

      <div className=" border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--isp-glass-border)] flex items-center justify-between">
          <h2 className="text-[var(--isp-heading)] font-semibold">Subscriptions</h2>
          {subsLoading ? <span className="text-xs text-[var(--isp-body)]">Loading...</span> : null}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-950/40 text-[var(--isp-body)]">
              <tr><Th>Email</Th><Th>Status</Th><Th>Plan</Th><Th>Interval</Th><Th>Currency</Th><Th>Next payment</Th></tr>
            </thead>
            <tbody>
              {(subsData.rows || []).slice(0, 200).map((r) => (
                <tr key={`${r.user_id}-${r.plan_id}`} className="border-t border-[var(--isp-glass-border)] hover:bg-[var(--isp-glass-inner)]">
                  <Td>{r.email}</Td><Td>{r.status}</Td><Td>{r.plan_id}</Td><Td>{r.interval || '—'}</Td><Td>{r.currency || '—'}</Td><Td>{r.next_payment_date || '—'}</Td>
                </tr>
              ))}
              {(!subsData.rows || subsData.rows.length === 0) && (
                <tr><td colSpan={6} className="text-center py-10 text-[var(--isp-body)]">No subscriptions</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className=" border border-[var(--isp-glass-border)] bg-slate-900/60 backdrop-blur-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[var(--isp-glass-border)] flex items-center justify-between">
          <h2 className="text-[var(--isp-heading)] font-semibold">Paystack webhook events</h2>
          {eventsLoading ? <span className="text-xs text-[var(--isp-body)]">Loading...</span> : null}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-950/40 text-[var(--isp-body)]">
              <tr><Th>Event</Th><Th>Email</Th><Th>Reference</Th><Th>Currency</Th><Th>Amount</Th><Th>Created</Th></tr>
            </thead>
            <tbody>
              {(eventsData.rows || []).slice(0, 200).map((r) => (
                <tr key={`${r.reference || 'evt'}-${r.created_at}`} className="border-t border-[var(--isp-glass-border)] hover:bg-[var(--isp-glass-inner)]">
                  <Td>{r.event}</Td><Td>{r.customer_email || '—'}</Td><Td>{r.reference || '—'}</Td>
                  <Td>{r.currency || '—'}</Td><Td>{r.amount ?? '—'}</Td><Td>{r.created_at ? new Date(r.created_at).toLocaleString() : '—'}</Td>
                </tr>
              ))}
              {(!eventsData.rows || eventsData.rows.length === 0) && (
                <tr><td colSpan={6} className="text-center py-10 text-[var(--isp-body)]">No events stored yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
